import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const FR_MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const FR_DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

function startOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth(), 1);
}
function ymd(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Custom month-grid calendar — lighter than react-big-calendar and matching brand.
 * Props :
 *   - sessions : array of { id, formation_name, start_date (YYYY-MM-DD), city, cursus_slug }
 *   - onSessionClick : (session) => void
 */
export default function MiniCalendar({ sessions, onSessionClick }) {
    const initialDate = useMemo(() => {
        const today = new Date();
        if (!sessions || sessions.length === 0) return today;
        const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
        // If any session in current month → keep today, else jump to nearest future session month
        const sortedFuture = sessions
            .map((s) => s.start_date)
            .filter((d) => d)
            .sort();
        const inCurrent = sortedFuture.some((d) => d.startsWith(todayKey));
        if (inCurrent) return today;
        const future = sortedFuture.find((d) => d >= today.toISOString().slice(0, 10));
        const target = future || sortedFuture[sortedFuture.length - 1];
        if (!target) return today;
        const [y, m] = target.split("-");
        return new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    }, [sessions]);

    const [current, setCurrent] = useState(initialDate);

    const cells = useMemo(() => {
        const first = startOfMonth(current);
        const dayOfWeek = (first.getDay() + 6) % 7; // Mon=0
        const start = new Date(first);
        start.setDate(start.getDate() - dayOfWeek);
        const out = [];
        for (let i = 0; i < 42; i++) {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            out.push(d);
        }
        return out;
    }, [current]);

    const byDay = useMemo(() => {
        const map = {};
        for (const s of sessions || []) {
            (map[s.start_date] = map[s.start_date] || []).push(s);
        }
        return map;
    }, [sessions]);

    const today = ymd(new Date());
    const monthLabel = `${FR_MONTHS[current.getMonth()]} ${current.getFullYear()}`;

    return (
        <div className="rounded-2xl border border-brand-line bg-white p-5 shadow-premium" data-testid="formateur-mini-calendar">
            <div className="flex items-center justify-between">
                <h3 className="font-display text-2xl text-brand-ink">{monthLabel}</h3>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setCurrent(new Date(current.getFullYear(), current.getMonth() - 1, 1))}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-line hover:border-brand-orange hover:text-brand-orange"
                        aria-label="Mois précédent"
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button
                        onClick={() => setCurrent(new Date())}
                        className="rounded-full border border-brand-line px-3 py-1 text-xs font-medium hover:border-brand-orange hover:text-brand-orange"
                    >
                        Aujourd'hui
                    </button>
                    <button
                        onClick={() => setCurrent(new Date(current.getFullYear(), current.getMonth() + 1, 1))}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-line hover:border-brand-orange hover:text-brand-orange"
                        aria-label="Mois suivant"
                    >
                        <ChevronRight className="h-4 w-4" />
                    </button>
                </div>
            </div>
            <div className="mt-5 grid grid-cols-7 gap-px overflow-hidden rounded-xl border border-brand-line bg-brand-line">
                {FR_DAYS.map((d) => (
                    <div key={d} className="bg-[#FAFAFA] py-2 text-center text-[10px] font-semibold uppercase tracking-wider text-brand-smoke">
                        {d}
                    </div>
                ))}
                {cells.map((d, i) => {
                    const key = ymd(d);
                    const inMonth = d.getMonth() === current.getMonth();
                    const isToday = key === today;
                    const items = byDay[key] || [];
                    return (
                        <div
                            key={i}
                            data-testid={`cal-cell-${key}`}
                            className={`min-h-[88px] bg-white p-2 ${inMonth ? "" : "bg-[#FCFAF7] text-brand-smoke/60"}`}
                        >
                            <div className={`text-xs font-semibold ${isToday ? "inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-orange text-white" : "text-brand-ink/70"}`}>
                                {d.getDate()}
                            </div>
                            <div className="mt-1 space-y-1">
                                {items.map((s) => (
                                    <button
                                        key={s.id}
                                        onClick={() => onSessionClick?.(s)}
                                        data-testid={`cal-session-${s.id}`}
                                        className="block w-full truncate rounded-md bg-brand-orange/10 px-1.5 py-0.5 text-left text-[10px] font-medium text-brand-orange hover:bg-brand-orange hover:text-white"
                                        title={`${s.formation_name} · ${s.city}`}
                                    >
                                        {s.formation_name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
